import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  adjustUnifiedWallet,
  adjustWalletBalance,
  getWalletByLegacyUser,
  type WalletData,
} from "@/apis/admin/wallet-apis";
import { queryKeys } from "./keys";

/**
 * Hook to fetch wallet data by legacy user ID
 */
export function useWalletByLegacyUser(
  userId: number | null,
  cityId?: number,
  options?: { enabled?: boolean }
) {
  return useQuery<WalletData>({
    queryKey: queryKeys.wallets.byLegacyUser(userId!, cityId),
    queryFn: () => getWalletByLegacyUser(userId!, cityId, true),
    enabled: options?.enabled !== false && userId !== null,
  });
}

/**
 * Hook to adjust wallet balance with automatic cache invalidation
 */
export function useAdjustWalletBalance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adjustWalletBalance,
    onSuccess: (_data, variables) => {
      // Invalidate wallet queries for this user
      queryClient.invalidateQueries({
        queryKey: queryKeys.wallets.all,
        predicate: (query) => {
          const key = query.queryKey;
          return (
            key[0] === "wallets" &&
            key[1] === "byLegacyUser" &&
            key[2] === Number(variables.userId)
          );
        },
      });
    },
  });
}

/**
 * Hook to adjust a migrated user's unified wallet directly (synchronous), with
 * automatic cache invalidation for that user's wallet query.
 */
export function useAdjustUnifiedWallet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adjustUnifiedWallet,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.wallets.all,
        predicate: (query) => {
          const key = query.queryKey;
          return (
            key[0] === "wallets" &&
            key[1] === "byLegacyUser" &&
            key[2] === Number(variables.legacyUserId)
          );
        },
      });
    },
  });
}
